<section class="workspace-section">
    <section class="financial-position" aria-label="Shipment financial position">
        <div class="financial-position-heading">
            <div>
                <span>Financial position</span>
                <h3>Revenue, cost, and margin at a glance</h3>
                <p>Amounts include the shipment base price and additional billable charge lines.</p>
            </div>
            <p-tag [value]="shipmentAccountingRouteLabel()" [severity]="shipmentAccountingRouteSeverity()"></p-tag>
        </div>

        <div class="financial-position-cards">
            <article class="financial-position-card is-revenue">
                <div class="financial-card-icon"><i class="fa-solid fa-arrow-trend-up"></i></div>
                <span>Customer revenue</span>
                <strong>{{ formatShipmentAmount(shipmentRevenueTotal()) }}</strong>
                <small>Customer base price plus billable charges</small>
            </article>
            <article class="financial-position-card is-cost">
                <div class="financial-card-icon"><i class="fa-solid fa-truck-fast"></i></div>
                <span>Total cost</span>
                <strong>{{ formatShipmentAmount(shipmentCostTotal()) }}</strong>
                <small>Base buying price plus transport and other costs</small>
            </article>
            <article class="financial-position-card is-margin" [class.is-negative]="commercialMargin() < 0">
                <div class="financial-card-icon"><i class="fa-solid fa-scale-balanced"></i></div>
                <span>Gross margin</span>
                <strong>{{ formatShipmentAmount(commercialMargin()) }}</strong>
                <small>{{ shipmentMarginPercentageLabel() }}</small>
            </article>
            <article class="financial-position-card is-accounting">
                <div class="financial-card-icon"><i class="fa-solid fa-building-columns"></i></div>
                <span>Posting impact</span>
                <strong>{{ shipmentAccountingRouteTitle() }}</strong>
                <small>{{ shipmentAccountingRouteDetail() }}</small>
            </article>
        </div>

        <div class="financial-breakdown">
            <div class="financial-breakdown-group is-revenue">
                <div class="financial-breakdown-heading"><i class="fa-solid fa-money-bill-trend-up"></i><div><span>Revenue breakdown</span><strong>What the customer is billed</strong></div></div>
                <dl>
                    <div><dt>Base customer price</dt><dd>{{ formatShipmentAmount(shipmentBaseSellingTotal()) }}</dd></div>
                    <div><dt>Additional billable charge lines</dt><dd>{{ formatShipmentAmount(supplementalSellingCharges()) }}</dd></div>
                </dl>
            </div>
            <div class="financial-breakdown-group is-cost">
                <div class="financial-breakdown-heading"><i class="fa-solid fa-receipt"></i><div><span>Cost breakdown</span><strong>What this shipment costs to fulfil</strong></div></div>
                <dl>
                    <div><dt>Delivery transport cost</dt><dd>{{ formatShipmentAmount(shipmentTransportChargeTotal()) }}</dd></div>
                    <div><dt>Other cost charge lines</dt><dd>{{ formatShipmentAmount(shipmentOtherBuyingChargeTotal()) }}</dd></div>
                </dl>
            </div>
            <div class="financial-breakdown-group is-operation">
                <div class="financial-breakdown-heading"><i class="fa-solid fa-route"></i><div><span>Transport cost setup</span><strong>{{ shipmentTransportSourceSummary() }}</strong></div></div>
                <dl>
                    <div><dt>Internal transport assigned</dt><dd>{{ formatShipmentAmount(shipmentAssignedInternalCostTotal()) }}</dd></div>
                    <div><dt>Supplier transport assigned</dt><dd>{{ formatShipmentAmount(shipmentAssignedExternalCostTotal()) }}</dd></div>
                </dl>
            </div>
        </div>
    </section>

    <div class="pricing-source-panel">
        <div class="pricing-source-card">
            <div class="pricing-source-heading"><div><span>Delivery transport finance</span><strong>Driver-owned accounting</strong></div><p-tag value="Delivery snapshots" severity="info"></p-tag></div>
            <div class="pricing-source-grid">
                @for (row of deliveryFinanceRows(); track row.deliveryNumber) {
                    <div><span>{{ row.deliveryNumber }}</span><strong>{{ row.channel }}</strong><small>{{ row.payee }} · {{ row.currency }} {{ row.amount | number:'1.2-2' }}</small></div>
                } @empty {
                    <div><span>Assignments</span><strong>Not captured</strong><small>Assign a driver and confirm transport cost.</small></div>
                }
            </div>
        </div>

        <div class="pricing-source-card">
            <div class="pricing-source-heading"><div><span>Selling Source</span><strong>{{ sellingPriceSourceLabel() }}</strong></div><p-tag [value]="sellingPriceSourceLabel()" [severity]="sellingPriceSourceSeverity()"></p-tag></div>
            <div class="pricing-source-grid compact">
                <div><span>Current Selling</span><strong>{{ currentSellingPriceLabel() }}</strong><small>Customer base rate</small></div>
                <div><span>Customer Contract</span><strong>{{ customerPricingContractLabel() }}</strong><small>{{ customerPricingRateCardLabel() }}</small></div>
				<div><span>Commercial Payment Term</span><strong>{{ shipment().paymentTerm?.name || 'Not set' }}</strong><small>{{ shipment().paymentTerm?.days ?? 0 }} day{{ shipment().paymentTerm?.days === 1 ? '' : 's' }}</small></div>
            </div>
        </div>
    </div>

    @if (hasLegacyPricingContext()) {
        <details class="pricing-source-card legacy-pricing-context">
            <summary>Legacy shipment pricing context (historical only)</summary>
            <div class="pricing-source-grid compact">
                <div><span>Legacy buying source</span><strong>{{ buyingPriceSourceLabel() }}</strong><small>Not used for delivery finance or Accounts posting.</small></div>
                <div><span>Legacy pricing reference</span><strong>{{ supplierPricingReferenceLabel() }}</strong><small>{{ internalPricingReferenceLabel() }}</small></div>
            </div>
        </details>
    }

    <div class="charge-ledger">
        <div class="table-caption table-caption-actions charge-ledger-caption">
            <div><strong>Charge Lines</strong><span>{{ shipment().chargeLines?.length ?? 0 }} commercial adjustments</span></div>
            @if (canEditCharges()) { <p-button label="Add Charge" severity="help" icon="fa-solid fa-plus" (onClick)="command.emit({ type: 'add-charge' })"></p-button> }
        </div>

        @if ((shipment().chargeLines ?? []).length > 0) {
            <div class="charge-ledger-list">
                @for (chargeLine of shipment().chargeLines; track chargeLine.chargeLineId || chargeLine.chargeType) {
                    <article class="charge-ledger-row">
                        <div class="charge-ledger-main">
                            <div>
                                <span>{{ chargeLine.chargeSource === 'PRODUCT' ? 'Catalog Service' : 'Charge Type' }}</span>
                                <strong>{{ chargeLine.chargeSource === 'PRODUCT' ? (chargeLine.productName + (chargeLine.variantName ? ' - ' + chargeLine.variantName : '')) : formatChargeType(chargeLine) }}</strong>
                                @if (chargeLine.chargeSource === 'PRODUCT') { <small>{{ chargeLine.productCode || 'No product code' }} | {{ formatChargeType(chargeLine) }}</small> }
                                <small>{{ chargeLine.remarks || 'No remarks added' }}</small>
                            </div>
                            <div class="charge-ledger-tags">
                                @if (chargeLine.buyingPriceOverridden || chargeLine.sellingPriceOverridden) { <p-tag value="Price override" severity="warn"></p-tag> }
                                <p-tag [value]="chargeDeliveryLabel(chargeLine)" severity="info"></p-tag>
                            </div>
                        </div>
                        <div class="charge-ledger-grid">
                            <div><span>Buying</span><strong class="amount-buying">{{ formatChargeAmount(chargeLine, chargeLine.buyingChargeAmount) }}</strong><small>{{ chargeLine.buyingPriceOverridden ? 'Catalog rate overridden' : 'Carrier cost' }}</small></div>
                            <div><span>Selling</span><strong class="amount-selling">{{ formatChargeAmount(chargeLine, chargeLine.sellingChargeAmount) }}</strong><small>{{ chargeLine.sellingPriceOverridden ? 'Catalog rate overridden' : 'Customer billing' }}</small></div>
                            <div><span>Margin</span><strong [class.amount-selling]="chargeMargin(chargeLine) >= 0" [class.amount-buying]="chargeMargin(chargeLine) < 0">{{ formatChargeAmount(chargeLine, chargeMargin(chargeLine)) }}</strong><small>{{ chargeMarginPercent(chargeLine) }}</small></div>
                            <div><span>Effective</span><strong>{{ showDate(chargeLine.effectiveDate) || 'Not dated' }}</strong><small>{{ chargeLine.deliveryId ? 'Delivery level' : 'Shipment level' }}</small></div>
                        </div>
                        @if (canEditCharges()) { <div class="charge-ledger-actions"><button pButton type="button" label="Edit Charge" icon="fa-solid fa-pen" severity="secondary" [outlined]="true" pTooltip="Edit charge" (click)="command.emit({ type: 'edit-charge', chargeLine })"></button></div> }
                    </article>
                }
            </div>
        } @else {
            <div class="inline-empty charge-ledger-empty">No charge lines have been added.</div>
        }
    </div>

    @if (shipment().shipmentId) { <app-trip-cost-summary [shipmentId]="shipment().shipmentId!" [refreshKey]="tripCostRefreshKey()"></app-trip-cost-summary> }
</section>
